import {
    Box,
    Button,
    Chip,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import {
    useDataProvider,
    useLocaleState,
    useNotify,
    useRecordContext,
    useTranslate,
    useTheme,
} from "react-admin";
import { handleHttpError } from "shared/src/utils/errorHandler";
import Link from "@mui/material/Link";

export const EdispoDiffTable = ({ equipData }) => {
    const [locale] = useLocaleState();
    const translate = useTranslate();
    const notify = useNotify();
    const dataProvider = useDataProvider();
    const rental = useRecordContext();
    const [isOldData, setIsOldData] = useState(true);
    const [edispoItems, setEdispoItems] = useState(rental.edispoItemData || []);
    const [tableData, setTableData] = useState([]);
    const [filteredTableData, setFilteredTableData] = useState([]);
    const [hideHeader, setHideHeader] = useState(false);
    const tableContainerRef = useRef(null);
    const [theme] = useTheme();
    const [nameFilter, setNameFilter] = useState("");
    const [debouncedNameFilter, setDebouncedNameFilter] = useState("");
    const [stateFilter, setStateFilter] = useState("");

    const mergeData = useCallback(
        (edispoData, equipData) => {
            if (!rental.edispoId) {
                setTableData(equipData);

                return;
            }

            let myEdispoData = [...edispoData];
            const myEquipData = equipData.map((equipDataItem) => {
                const equipItem = { ...equipDataItem };
                delete equipItem.id;
                equipItem.status = [];

                if (!equipItem.product?.edispoId) {
                    equipItem.status = ["noEdispoProduct"];

                    return equipItem;
                }

                let edispoItem = null;
                myEdispoData.forEach((item, index) => {
                    if (
                        item.id !== equipItem.product?.edispoId ||
                        item.edispoType !== equipItem.product?.edispoType
                    ) {
                        return;
                    }

                    item.foundItem = true;
                    edispoItem = item;

                    if (edispoItem.added === edispoItem.required) {
                        equipItem.status.push("isFulfilled");
                    }

                    if (edispoItem.added > 0 && edispoItem.added < edispoItem.required) {
                        equipItem.status.push("fulfillmentInProgress");
                    }

                    if (edispoItem.dateDifferences.length > 0) {
                        equipItem.status.push("hasDateDifferences");
                    }

                    if (edispoItem.added > edispoItem.required) {
                        equipItem.status.push("scannedTooMany");
                    }
                });

                if (edispoItem) {
                    return {
                        ...equipItem,
                        ...edispoItem,
                    };
                }

                if (equipItem.product?.edispoId) {
                    equipItem.status.push("isNotInEdispoList");
                }

                return equipItem;
            });

            myEdispoData = myEdispoData.filter((item, index) => {
                if (!item.foundItem) {
                    item.status = ["missingItem"];

                    return true;
                }

                return false;
            });

            setTableData([...myEdispoData, ...myEquipData]);
        },
        [setTableData, rental.edispoId]
    );

    const getEdispoData = () => {
        if (!isOldData) {
            setEdispoItems(rental.edispoItemData);
            setIsOldData(true);
            return;
        }

        dataProvider
            .get(`/edispo/actualEdispoData/${rental.id}`, "rentals")
            .then((response) => {
                setEdispoItems(response.data.edispoItemData);
                setIsOldData(false);
            })
            .catch((e) => {
                handleHttpError(e, notify);
            });
    };

    const columns = [
        {
            id: `product`,
            label: translate("equip.name"),
            format: (value) => value?.name?.[locale] || "",
        },
        { id: "labelNumber", label: translate("equip.labelNumber") },
        { id: "quantity", label: translate("equip.quantity") },
        {
            id: "rentalProcess",
            label: translate("equip.rentedOut"),
            format: (value) => {
                if (!value) return "";
                return (
                    <Link
                        href="#"
                        onClick={(event) => {
                            event.preventDefault();
                            dataProvider.getDownload("rentalProcesses", {
                                id: value.id,
                                contentType: "application/pdf",
                            });
                        }}
                        title={translate("equip.downloadPdf") + " (" + value.renter?.fullName + ")"}
                        label={translate("equip.downloadPdf") + " (" + value.renter?.fullName + ")"}
                    >
                        {value.signatureDate?.format("DD.MM.YYYY HH:mm:ss")}
                    </Link>
                );
            },
        },
        {
            id: "earliestPlannedReturnDate",
            label: translate("equip.plannedReturnDate"),
            format: (value) => value?.format("DD.MM.YYYY") || "",
        },
        { id: "returnedQuantity", label: translate("equip.returned") },
        { id: "openQuantity", label: translate("equip.openQuantity") },
        { id: "lostQuantity", label: translate("equip.lostQuantity") },
        { id: "defectQuantity", label: translate("equip.defectQuantity") },
        { id: "delayedQuantity", label: translate("equip.delayedQuantity") },
        {
            id: "status",
            label: translate("equip.status"),
            format: (value) =>
                value?.map((status, index) => (
                    <Chip
                        key={`status_${index}`}
                        sx={{
                            backgroundColor: statusInfo[status].color,
                            ...(index > 0 && { marginTop: "3px" }),
                        }}
                        label={statusInfo[status].label}
                    />
                )),
        },
    ];

    if (rental.edispoId) {
        columns.unshift(
            { id: "name", label: translate("equip.name") },
            { id: "id", label: translate("ID") },
            { id: "edispoType", label: translate("equip.edispo.type") },
            { id: "required", label: translate("equip.quantity") },
            {
                id: "returnDate",
                label: translate("equip.returnDate"),
                format: (value) => value?.format("DD.MM.YYYY") || "",
            }
        );
    }

    const statusInfo = {
        noEdispoProduct: {
            color: "#4699ff52",
            label: translate("equip.noEdispoProduct"),
        },
        isNotInEdispoList: {
            color: "#ff4d4d70",
            label: translate("equip.notInEdispoList"),
        },
        isFulfilled: {
            color: "#86ff4d70",
            label: translate("equip.fulfilled"),
        },
        fulfillmentInProgress: {
            color: "#ff9f4d70",
            label: translate("equip.fulfillmentInProgress"),
        },
        hasDateDifferences: {
            color: "#ff9f4d70",
            label: translate("equip.dateDifferences"),
        },
        scannedTooMany: {
            color: "#ff4d4d70",
            label: translate("equip.scannedTooMany"),
        },
        missingItem: {
            color: "#ff4d4d70",
            label: translate("equip.missingItem"),
        },
    };

    useEffect(() => {
        const handleScroll = () => {
            const scrollPosition = window.scrollY;
            if (scrollPosition >= 344) {
                setHideHeader(true);
            } else {
                setHideHeader(false);
            }
        };

        window.addEventListener("scroll", handleScroll);

        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    useEffect(() => {
        const header = document.querySelector("header");
        if (header) {
            header.style.display = hideHeader ? "none" : "block";
        }
    }, [hideHeader]);

    useEffect(() => {
        mergeData(edispoItems, equipData);
    }, [edispoItems, equipData, mergeData]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedNameFilter(nameFilter);
        }, 300);

        return () => clearTimeout(timer);
    }, [nameFilter]);

    useEffect(() => {
        let filtered = [...tableData];

        if (debouncedNameFilter) {
            filtered = filtered.filter((row) => {
                const productName = row.product?.name?.[locale] || row.name || "";
                return productName.toLowerCase().includes(debouncedNameFilter.toLowerCase());
            });
        }

        if (stateFilter) {
            filtered = filtered.filter((row) => {
                const rentalItemState = row.state || "";
                return rentalItemState === stateFilter;
            });
        }

        setFilteredTableData(filtered);
    }, [tableData, debouncedNameFilter, stateFilter, locale]);

    const stateChoices = [
        { id: "", name: "equip.all" },
        { id: "open", name: "equip.open" },
        { id: "partially_returned", name: "equip.partially_returned" },
        { id: "returned", name: "equip.returned" },
        { id: "closed", name: "equip.closed" },
    ];

    return (
        <>
            <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}>
                <Box
                    sx={{
                        display: "block",
                        mt: 2
                    }}>
                    {rental.edispoId && (
                        <Button onClick={getEdispoData}>
                            {translate(
                                isOldData
                                    ? "equip.compareWithActualData"
                                    : "equip.compareWithOldData"
                            )}
                        </Button>
                    )}
                    <Box
                        sx={{
                            display: "flex",
                            gap: 2,
                            mt: 2
                        }}>
                        <TextField
                            size="small"
                            label={translate("equip.name")}
                            value={nameFilter}
                            onChange={(e) => setNameFilter(e.target.value)}
                            sx={{ width: 320 }}
                        />
                        <FormControl size="small" sx={{ width: 320 }}>
                            <InputLabel id="state-filter-label">
                                {translate("equip.state")}
                            </InputLabel>
                            <Select
                                labelId="state-filter-label"
                                value={stateFilter}
                                label={translate("equip.state")}
                                title={translate("equip.state")}
                                onChange={(e) => setStateFilter(e.target.value)}
                            >
                                {stateChoices.map((choice) => (
                                    <MenuItem key={choice.id} value={choice.id}>
                                        {translate(choice.name)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>
            </Box>
            <Paper
                sx={{
                    margin: 1.5,
                    position: "relative",
                    "> div:first-of-type": {
                        overflow: "unset !important",
                    },
                    minWidth: "1000px",
                }}
            >
                <TableContainer ref={tableContainerRef}>
                    <Table>
                        <TableHead
                            sx={{
                                position: "sticky",
                                width: "100%",
                                top: 0,
                                zIndex: 10,
                                backgroundColor: theme === "dark" ? "#1e1e1e" : "#fff",
                            }}
                        >
                            {rental.edispoId && (
                                <TableRow key={"h-0"}>
                                    <TableCell
                                        sx={{
                                            borderRight: "1px solid #d0cdcc",
                                            fontWeight: "bolder",
                                        }}
                                        align="center"
                                        colSpan={5}
                                        key={"h-0-col-0"}
                                    >
                                        Edispo
                                    </TableCell>
                                    <TableCell
                                        sx={{ fontWeight: "bolder" }}
                                        align="center"
                                        colSpan={11}
                                        key={"h-0-col-1"}
                                    >
                                        Equip
                                    </TableCell>
                                </TableRow>
                            )}
                            <TableRow key={"h-1"}>
                                {columns.map((column, index) => {
                                    return (
                                        <TableCell
                                            key={`h-1-col-${index}`}
                                            sx={{
                                                ...(column.id === "returnDate" && {
                                                    borderRight: "1px solid #d0cdcc",
                                                }),
                                                fontWeight: "bolder",
                                            }}
                                        >
                                            {column.label}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredTableData.map((row, index) => (
                                <TableRow hover tabIndex={-1} key={`row_${index}`}>
                                    {columns.map((column, i) => {
                                        const value = row[column.id];
                                        return (
                                            <TableCell
                                                key={`row_${index}_col_${i}`}
                                                sx={{
                                                    ...(column.id === "returnDate" && {
                                                        borderRight: "1px solid #d0cdcc",
                                                    }),
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        ...(column.id === "status" && {
                                                            display: "flex",
                                                            flexDirection: "column",
                                                        }),
                                                    }}
                                                >
                                                    {value === undefined
                                                        ? ""
                                                        : column.format
                                                        ? column.format(value)
                                                        : value}
                                                </Box>
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </>
    );
};
